import React, { useEffect, useMemo, useState } from "react";
import { X, ArrowLeft, ArrowRight, Play, Loader2, Server, Check } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import StorageEndpointPicker from "@/components/StorageEndpointPicker";

// A "nas"/"s3"/"gcs"/"azure"-kind agent record is just stored connection
// details — nothing installs software on it, so browsing/transferring on
// its behalf requires a real installed agent (workerAgentId) to connect for it.
const NAS_CONNECTION_KINDS = new Set(["nas", "s3", "gcs", "azure"]);

const STEPS = [
    { key: "source", label: "Source" },
    { key: "destination", label: "Destination" },
    { key: "data", label: "Select Data" },
    { key: "options", label: "Options" },
    { key: "review", label: "Review" },
];

/**
 * NewDataMigrationWizard — step-by-step replacement for the old single-
 * screen NewMigrationDialog. Same backend call (POST /storage-migrations),
 * same StorageEndpointPicker/WorkerAgentPicker/BandwidthSlider underneath —
 * this only changes how those existing pieces are presented, spread across
 * five focused steps instead of one crowded form, plus surfaces three
 * backend options (verify_integrity, compression, dedupe_policy) that
 * already worked but were never exposed in the UI before.
 */
export default function NewDataMigrationWizard({ onClose, onCreated }) {
    const [stepIdx, setStepIdx] = useState(0);
    const [agents, setAgents] = useState([]);
    const [name, setName] = useState("");
    const [nameTouched, setNameTouched] = useState(false);
    const [sourceId, setSourceId] = useState("");
    const [sourcePath, setSourcePath] = useState("/");
    const [destId, setDestId] = useState("");
    const [destPath, setDestPath] = useState("/");
    const [workerId, setWorkerId] = useState("");
    const [bandwidthCap, setBandwidthCap] = useState(0); // 0 == unlimited
    const [verifyIntegrity, setVerifyIntegrity] = useState(true);
    const [compression, setCompression] = useState(true);
    const [dedupePolicy, setDedupePolicy] = useState("manual");
    // Storage Gateway (/jobs) options — only shown/sent when needsWorker,
    // since storage_migrations.py has no versioning/mirror-deletes concept
    // and /jobs has no dedupe/verify-integrity concept.
    const [enableVersioning, setEnableVersioning] = useState(true);
    const [mirrorDeletes, setMirrorDeletes] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        http.get("/agents").then(({ data }) => setAgents(data)).catch(() => {});
    }, []);

    const sourceAgent = agents.find((a) => a.id === sourceId);
    const destAgent = agents.find((a) => a.id === destId);
    const sourceNeedsWorker = NAS_CONNECTION_KINDS.has(sourceAgent?.kind);
    const destNeedsWorker = NAS_CONNECTION_KINDS.has(destAgent?.kind);
    const needsWorker = sourceNeedsWorker || destNeedsWorker;
    const realAgents = useMemo(
        () => agents.filter((a) => !NAS_CONNECTION_KINDS.has(a.kind)),
        [agents],
    );

    // Dropping a NAS-kind pick leaves a stale worker selection behind.
    useEffect(() => {
        if (!needsWorker) setWorkerId("");
    }, [needsWorker]);

    // §23: sensible default name once both endpoints are known, editable
    // (and never overwritten again) the moment the user types their own.
    useEffect(() => {
        if (nameTouched) return;
        if (sourceAgent && destAgent) {
            setName(`${sourceAgent.name || sourceAgent.hostname || "source"} → ${destAgent.name || destAgent.hostname || "destination"}`);
        }
    }, [sourceAgent, destAgent, nameTouched]);

    // Esc to close + body-scroll lock while the wizard is up -- matches the
    // convention every other modal in the app follows (DemoRequestModal,
    // SATenantActions).
    useEffect(() => {
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [onClose]);

    const step = STEPS[stepIdx];
    const isLast = stepIdx === STEPS.length - 1;

    // §13: don't let the user configure an entire migration only to
    // discover at the end the combination can't be used — a worker agent
    // is required the moment either endpoint is a storage connection, so
    // that's gated right on the Destination step rather than deferred to
    // Review.
    const canAdvance =
        step.key === "source" ? !!sourceId :
        step.key === "destination" ? !!destId && (!needsWorker || !!workerId) :
        true;

    const goNext = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
    const goBack = () => setStepIdx((i) => Math.max(0, i - 1));

    const submit = async () => {
        setSubmitting(true);
        try {
            if (needsWorker) {
                // Storage Gateway: the real, PQC-encrypted agent-to-agent
                // pipeline (POST /jobs), not storage_migrations.py's
                // unencrypted same-box copy engine. source_agent_id/
                // dest_agent_id must always be a REAL installed agent (the
                // "gateway"); source_storage_id/dest_storage_id carries the
                // connection-record id for whichever side isn't one. If
                // BOTH sides are connection records, the single worker
                // picked below acts as gateway for both — matching
                // storage_migrations.py's own one-worker model, a
                // deliberate simplification of this wizard's single-picker
                // UI (two DIFFERENT gateways per side isn't expressible
                // here yet).
                const { data } = await http.post("/jobs", {
                    name: name || undefined,
                    source_path: sourcePath,
                    destination_path: destPath,
                    source_agent_id: sourceNeedsWorker ? workerId : sourceId,
                    dest_agent_id: destNeedsWorker ? workerId : destId,
                    source_storage_id: sourceNeedsWorker ? sourceId : undefined,
                    dest_storage_id: destNeedsWorker ? destId : undefined,
                    migration_kind: "storage",
                    mode: "one_shot",
                    // total_files/total_bytes omitted — JobCreateIn defaults
                    // to placeholder values (500 files / 1 GiB); the real
                    // scan happens post-claim, agent-side, same as this
                    // wizard's own Select Data step already tells the user.
                    options: {
                        bandwidth_limit_mbps: bandwidthCap > 0 ? bandwidthCap : null,
                        retries: 3,
                        retry_backoff_ms: 400,
                        compression,
                        encryption: true,
                        enable_versioning: enableVersioning,
                        mirror_deletes: mirrorDeletes,
                    },
                });
                toast.success("Gateway transfer started — encrypting agent-to-agent");
                onCreated(data.id, "jobs");
            } else {
                const { data } = await http.post("/storage-migrations", {
                    source_agent_id: sourceId,
                    dest_agent_id: destId,
                    source_path: sourcePath,
                    dest_path: destPath,
                    name: name || undefined,
                    options: {
                        bandwidth_cap_mbps: bandwidthCap > 0 ? bandwidthCap : null,
                        verify_integrity: verifyIntegrity,
                        compression,
                        dedupe_policy: dedupePolicy,
                    },
                });
                toast.success("Data migration started — scanning source");
                onCreated(data.id, "storage-migrations");
            }
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            data-testid="nas-wizard-backdrop"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label="New Data Migration"
                className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-border bg-surface"
                data-testid="nas-wizard"
            >
                <div className="flex items-center justify-between border-b border-border px-6 py-4">
                    <div>
                        <h2 className="font-display text-xl font-bold">New Data Migration</h2>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            Step {stepIdx + 1} of {STEPS.length} — {step.label}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="nas-wizard-close"
                        aria-label="Close"
                        className="rounded-sm border border-border p-1.5 hover:border-foreground/40"
                    >
                        <X size={13} />
                    </button>
                </div>

                <StepIndicator steps={STEPS} current={stepIdx} />

                <div className="flex-1 overflow-y-auto px-6 py-5" data-testid={`nas-wizard-step-${step.key}`}>
                    {step.key === "source" && (
                        <StorageEndpointPicker
                            label="Source"
                            role="source"
                            testid="nas-source"
                            agents={agents}
                            onAgentsChange={setAgents}
                            agentId={sourceId}
                            onAgentChange={setSourceId}
                            path={sourcePath}
                            onPathChange={setSourcePath}
                            workerAgentId={workerId}
                        />
                    )}

                    {step.key === "destination" && (
                        <div className="space-y-4">
                            <StorageEndpointPicker
                                label="Destination"
                                role="destination"
                                testid="nas-dest"
                                agents={agents}
                                onAgentsChange={setAgents}
                                agentId={destId}
                                onAgentChange={setDestId}
                                path={destPath}
                                onPathChange={setDestPath}
                                workerAgentId={workerId}
                            />
                            {needsWorker && (
                                <WorkerAgentPicker
                                    agents={realAgents}
                                    workerId={workerId}
                                    onWorkerChange={setWorkerId}
                                    sourceIsNas={NAS_CONNECTION_KINDS.has(sourceAgent?.kind)}
                                    destIsNas={NAS_CONNECTION_KINDS.has(destAgent?.kind)}
                                />
                            )}
                        </div>
                    )}

                    {step.key === "data" && (
                        <SelectDataStep sourceAgent={sourceAgent} sourcePath={sourcePath} />
                    )}

                    {step.key === "options" && (
                        <OptionsStep
                            needsWorker={needsWorker}
                            bandwidthCap={bandwidthCap} onBandwidthChange={setBandwidthCap}
                            verifyIntegrity={verifyIntegrity} onVerifyChange={setVerifyIntegrity}
                            compression={compression} onCompressionChange={setCompression}
                            dedupePolicy={dedupePolicy} onDedupeChange={setDedupePolicy}
                            enableVersioning={enableVersioning} onVersioningChange={setEnableVersioning}
                            mirrorDeletes={mirrorDeletes} onMirrorDeletesChange={setMirrorDeletes}
                        />
                    )}

                    {step.key === "review" && (
                        <ReviewStep
                            needsWorker={needsWorker}
                            name={name}
                            onNameChange={(v) => { setName(v); setNameTouched(true); }}
                            sourceAgent={sourceAgent} sourcePath={sourcePath}
                            destAgent={destAgent} destPath={destPath}
                            bandwidthCap={bandwidthCap} verifyIntegrity={verifyIntegrity}
                            compression={compression} dedupePolicy={dedupePolicy}
                            enableVersioning={enableVersioning} mirrorDeletes={mirrorDeletes}
                        />
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-border px-6 py-4">
                    <Button variant="ghost" onClick={stepIdx === 0 ? onClose : goBack} data-testid="nas-wizard-back">
                        {stepIdx === 0 ? "Cancel" : (<><ArrowLeft size={14} className="mr-1.5" /> Back</>)}
                    </Button>
                    {isLast ? (
                        <Button
                            onClick={submit}
                            disabled={submitting || !sourceId || !destId}
                            data-testid="nas-wizard-start"
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
                            Start Migration
                        </Button>
                    ) : (
                        <Button
                            onClick={goNext}
                            disabled={!canAdvance}
                            data-testid="nas-wizard-next"
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            Next <ArrowRight size={14} className="ml-1.5" />
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ---- Step indicator ---------------------------------------------------------

function StepIndicator({ steps, current }) {
    return (
        <div className="flex items-center gap-1 border-b border-border px-6 py-3" data-testid="nas-wizard-steps">
            {steps.map((s, i) => (
                <React.Fragment key={s.key}>
                    <div className="flex items-center gap-1.5">
                        <div
                            className={[
                                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold",
                                i < current ? "bg-blue-500 text-white" :
                                i === current ? "border-2 border-blue-500 text-blue-400" :
                                "border border-border text-muted-foreground",
                            ].join(" ")}
                        >
                            {i < current ? <Check size={11} /> : i + 1}
                        </div>
                        <span
                            className={[
                                "hidden text-[11px] font-medium sm:inline",
                                i === current ? "text-foreground" : "text-muted-foreground",
                            ].join(" ")}
                        >
                            {s.label}
                        </span>
                    </div>
                    {i < steps.length - 1 && (
                        <div className={["mx-1 h-px flex-1", i < current ? "bg-blue-500/50" : "bg-border"].join(" ")} />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
}

// ---- Select Data step --------------------------------------------------------
// §15/§20: the backend migrates everything under one chosen root path (server-
// side scan + dedupe), not an arbitrary client-side multi-select of individual
// folders — there's no endpoint for that. This step confirms the path chosen
// in Source rather than faking a checkbox tree the backend can't act on, and
// is honest that the real numbers only exist once scanning starts.

function SelectDataStep({ sourceAgent, sourcePath }) {
    return (
        <div className="space-y-4">
            <div className="rounded-lg border border-border bg-background p-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Migrating from</div>
                <div className="mt-1 font-mono text-sm text-foreground" data-testid="nas-wizard-selected-path">
                    {sourceAgent?.name || sourceAgent?.hostname || "source"}:{sourcePath || "/"}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                    Everything under this path will be scanned and migrated. To transfer a different folder,
                    go back to the Source step and Browse to it.
                </p>
            </div>
            <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
                File count and total size are calculated once the migration starts scanning — you&apos;ll see
                live progress on the next screen after you click Start Migration.
            </div>
        </div>
    );
}

// ---- Options step -------------------------------------------------------------
// Only real, already-working backend options (see storage_migrations.py's
// `options` dict) — no controls for behavior the backend doesn't support.

function OptionsStep({
    needsWorker,
    bandwidthCap, onBandwidthChange,
    verifyIntegrity, onVerifyChange,
    compression, onCompressionChange,
    dedupePolicy, onDedupeChange,
    enableVersioning, onVersioningChange,
    mirrorDeletes, onMirrorDeletesChange,
}) {
    // Storage Gateway (/jobs) and the classic same-box engine
    // (/storage-migrations) have different, non-overlapping option
    // vocabularies -- dedupe/verify-integrity only exist on the classic
    // engine; versioning/mirror-deletes only exist on the real
    // PQC-encrypted pipeline. Bandwidth cap and compression are the only
    // two shared controls.
    return (
        <div className="space-y-5">
            {needsWorker ? (
                <OptionGroup title="Data Protection">
                    <Toggle
                        label="Keep previous versions on overwrite"
                        checked={enableVersioning}
                        onChange={onVersioningChange}
                        testid="nas-wizard-versioning"
                    />
                    <div className="mt-2">
                        <Toggle
                            label="Mirror deletes (remove files at destination that no longer exist at source)"
                            checked={mirrorDeletes}
                            onChange={onMirrorDeletesChange}
                            testid="nas-wizard-mirror-deletes"
                        />
                    </div>
                </OptionGroup>
            ) : (
                <>
                    <OptionGroup title="File Handling">
                        <label className="mb-1.5 block text-xs text-muted-foreground">When duplicate files are found</label>
                        <select
                            value={dedupePolicy}
                            onChange={(e) => onDedupeChange(e.target.value)}
                            data-testid="nas-wizard-dedupe-policy"
                            className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                        >
                            <option value="manual">Review manually after scan</option>
                            <option value="keep_oldest">Keep oldest automatically</option>
                            <option value="keep_newest">Keep newest automatically</option>
                        </select>
                    </OptionGroup>

                    <OptionGroup title="Verification">
                        <Toggle
                            label="Verify transferred data (SHA-256)"
                            checked={verifyIntegrity}
                            onChange={onVerifyChange}
                            testid="nas-wizard-verify"
                        />
                    </OptionGroup>
                </>
            )}

            <OptionGroup title="Performance">
                <BandwidthSlider value={bandwidthCap} onChange={onBandwidthChange} />
                <div className="mt-3">
                    <Toggle
                        label="Compress data in transit"
                        checked={compression}
                        onChange={onCompressionChange}
                        testid="nas-wizard-compression"
                    />
                </div>
            </OptionGroup>
        </div>
    );
}

function OptionGroup({ title, children }) {
    return (
        <div>
            <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{title}</div>
            {children}
        </div>
    );
}

function Toggle({ label, checked, onChange, testid }) {
    return (
        <label className="flex cursor-pointer items-center justify-between rounded border border-border bg-background px-3 py-2.5">
            <span className="text-sm">{label}</span>
            <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
        </label>
    );
}

// ---- Review step --------------------------------------------------------------

function ReviewStep({
    needsWorker,
    name, onNameChange,
    sourceAgent, sourcePath,
    destAgent, destPath,
    bandwidthCap, verifyIntegrity, compression, dedupePolicy,
    enableVersioning, mirrorDeletes,
}) {
    return (
        <div className="space-y-4">
            <div>
                <label className="mb-1.5 block text-xs text-muted-foreground">Migration Name</label>
                <input
                    value={name}
                    onChange={(e) => onNameChange(e.target.value)}
                    data-testid="nas-wizard-name-input"
                    className="w-full rounded border border-border bg-background px-3 py-2 text-sm"
                />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
                <ReviewCard label="Source" title={sourceAgent?.name || sourceAgent?.hostname} sub={sourcePath} />
                <ReviewCard label="Destination" title={destAgent?.name || destAgent?.hostname} sub={destPath} />
            </div>
            <div className="rounded-lg border border-border bg-background p-4">
                <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Options</div>
                <ul className="space-y-1.5 text-sm" data-testid="nas-wizard-review-options">
                    {needsWorker ? (
                        <>
                            <li className="flex items-center gap-2">
                                <Check size={13} className="shrink-0 text-emerald-400" />
                                {enableVersioning ? "Keep previous versions on overwrite" : "Overwrite without versioning"}
                            </li>
                            <li className="flex items-center gap-2">
                                <Check size={13} className="shrink-0 text-emerald-400" />
                                {mirrorDeletes ? "Mirror deletes" : "Never delete at destination"}
                            </li>
                        </>
                    ) : (
                        <>
                            <li className="flex items-center gap-2">
                                <Check size={13} className="shrink-0 text-emerald-400" />
                                {verifyIntegrity ? "Verify transferred data" : "Skip verification"}
                            </li>
                            <li className="flex items-center gap-2">
                                <Check size={13} className="shrink-0 text-emerald-400" />
                                Duplicates: {dedupePolicy === "manual" ? "Review manually" : dedupePolicy === "keep_oldest" ? "Keep oldest automatically" : "Keep newest automatically"}
                            </li>
                        </>
                    )}
                    <li className="flex items-center gap-2">
                        <Check size={13} className="shrink-0 text-emerald-400" />
                        {compression ? "Compress data in transit" : "No compression"}
                    </li>
                    <li className="flex items-center gap-2">
                        <Check size={13} className="shrink-0 text-emerald-400" />
                        Bandwidth: {bandwidthCap === 0 ? "Unlimited" : `${bandwidthCap} MB/s`}
                    </li>
                    {needsWorker && (
                        <li className="flex items-center gap-2 text-emerald-300">
                            <Check size={13} className="shrink-0 text-emerald-400" />
                            Encrypted end-to-end (quantum-safe agent-to-agent transport)
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
}

function ReviewCard({ label, title, sub }) {
    return (
        <div className="rounded-lg border border-border bg-background p-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-1 text-sm font-semibold" data-testid={`nas-wizard-review-${label.toLowerCase()}`}>{title || "—"}</div>
            <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">{sub || "/"}</div>
        </div>
    );
}

// ---- Gateway agent picker ------------------------------------------------------
// Relocated from StorageMigrationsNAS.jsx's old NewMigrationDialog. Now
// used for BOTH backends this wizard can submit to: for a plain real-agent
// pair it's never shown; the moment either side is a storage connection
// record, the picked agent becomes either storage_migrations.py's
// worker_agent_id (same-box copy) or, for the real PQC pipeline, the
// gateway agent that mounts/connects to that storage and speaks the
// encrypted agent-to-agent protocol on its behalf (source_agent_id/
// dest_agent_id in the /jobs submission above).

function WorkerAgentPicker({ agents, workerId, onWorkerChange, sourceIsNas, destIsNas }) {
    const side = sourceIsNas && destIsNas ? "source and destination" : sourceIsNas ? "source" : "destination";
    return (
        <div className="rounded border border-amber-400/30 bg-amber-500/[0.06] p-3" data-testid="nas-worker-picker">
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-100">
                <Server size={13} /> Gateway agent required
            </div>
            <p className="mt-1 text-[11px] text-amber-200/80">
                The {side} you picked is a storage connection, not installed software — it can&apos;t run
                a scan or transfer itself. Pick one of your installed PC/server agents to act as its
                gateway: it connects to the storage and carries out the encrypted transfer on its behalf.
            </p>
            <select
                value={workerId}
                onChange={(e) => onWorkerChange(e.target.value)}
                data-testid="nas-worker-agent-select"
                className="mt-2 w-full rounded border border-border bg-background px-3 py-2 text-sm"
            >
                <option value="">Pick a worker agent…</option>
                {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                        {`${a.name || a.hostname || a.id.slice(0, 8)} (${a.kind || "vm"})`}
                    </option>
                ))}
            </select>
            {agents.length === 0 && (
                <p className="mt-1.5 text-[11px] text-rose-300">
                    No installed agents available — deploy the MoovBytes agent on a PC or server first.
                </p>
            )}
        </div>
    );
}

// ---- Bandwidth-cap slider -------------------------------------------------------
// Relocated from StorageMigrationsNAS.jsx's old NewMigrationDialog — operator
// can throttle the migration so it doesn't saturate the NAS uplink during
// business hours. 0 means unlimited.

const BANDWIDTH_PRESETS = [
    { v: 0,    label: "∞" },
    { v: 50,   label: "50 MB/s" },
    { v: 100,  label: "100 MB/s" },
    { v: 250,  label: "250 MB/s" },
    { v: 500,  label: "500 MB/s" },
    { v: 1000, label: "1 GB/s" },
];

function BandwidthSlider({ value, onChange }) {
    const label = value === 0
        ? "Unlimited"
        : value >= 1000 ? `${(value / 1000).toFixed(1)} GB/s` : `${value} MB/s`;
    return (
        <div className="rounded border border-border bg-background p-3" data-testid="nas-bandwidth-slider">
            <div className="mb-2 flex items-center justify-between">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    Bandwidth cap
                </div>
                <div
                    data-testid="nas-bandwidth-value"
                    className="rounded bg-blue-500/10 px-2 py-0.5 font-mono text-[11px] text-blue-200 border border-blue-500/30"
                >
                    {label}
                </div>
            </div>
            <input
                type="range"
                min={0}
                max={1000}
                step={25}
                value={value}
                onChange={(e) => onChange(parseInt(e.target.value, 10))}
                data-testid="nas-bandwidth-range"
                className="w-full accent-blue-500"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
                {BANDWIDTH_PRESETS.map((p) => (
                    <button
                        key={p.v}
                        type="button"
                        onClick={() => onChange(p.v)}
                        data-testid={`nas-bandwidth-preset-${p.v}`}
                        className={[
                            "rounded border px-2 py-0.5 text-[11px]",
                            value === p.v
                                ? "border-blue-500 bg-blue-500/15 text-blue-200"
                                : "border-border text-muted-foreground hover:border-zinc-500",
                        ].join(" ")}
                    >
                        {p.label}
                    </button>
                ))}
            </div>
            <p className="mt-1.5 text-[10px] text-muted-foreground">
                Caps the aggregate transfer rate via a token-bucket on the agent. Useful for nighttime migrations on shared networks.
            </p>
        </div>
    );
}
